<script>
  import { useForm, inertia } from '@inertiajs/svelte';
  import Icon from '@iconify/svelte';
  import AppLayout from '../../components/AppLayout.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Field from '../../components/ui/Field.svelte';
  import Button from '../../components/ui/Button.svelte';
  import PlatformBadge from '../../components/ui/PlatformBadge.svelte';
  import ImageUploader from '../../components/ui/ImageUploader.svelte';

  // `source` is set when this page was reached via "Reuse" on an existing
  // post: it prefills the form but is never itself submitted, so the result
  // is a brand new post rather than an edit of the original.
  let { calendars = [], source = null } = $props();

  let scheduleLater = $state(false);
  let clientErrors = $state({});

  // Flat fields (no `post` key — that name collides with the form's .post()
  // method in Inertia). transform() nests them under `post` to match Rails'
  // params.require(:post).
  const form = useForm({
    body: source?.body ?? '',
    calendar_id: source?.calendar_id ?? calendars[0]?.id ?? null,
    social_account_ids: source?.social_account_ids ?? [],
    scheduled_at: '',
    media: source?.media ?? [],
  });

  let uploadingCount = $state(0);

  let selectedCalendar = $derived(calendars.find((c) => c.id === form.calendar_id) ?? null);
  let social_accounts = $derived(selectedCalendar?.social_accounts ?? []);

  function selectCalendar(id) {
    form.calendar_id = id;
    form.social_account_ids = [];
  }

  let allSelected = $derived(
    social_accounts.length > 0 && social_accounts.every((a) => form.social_account_ids.includes(a.id)),
  );

  function toggleAccount(id) {
    const ids = form.social_account_ids;
    form.social_account_ids = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
  }

  function toggleAll() {
    form.social_account_ids = allSelected ? [] : social_accounts.map((a) => a.id);
  }

  function validate() {
    clientErrors = {};
    if (scheduleLater && form.scheduled_at && new Date(form.scheduled_at) <= new Date()) {
      clientErrors.scheduled_at = 'Pick a time in the future.';
    }
    if (uploadingCount > 0) {
      clientErrors.media = 'Wait for images to finish uploading.';
    }
    return Object.keys(clientErrors).length === 0;
  }

  function submit() {
    if (!validate()) return;
    if (!scheduleLater) form.scheduled_at = '';
    form
      .transform((data) => ({ post: data }))
      .post('/posts');
  }

  // What the server refused to carry over from the original post, phrased as
  // one sentence so it can sit in a single banner.
  let droppedNotice = $derived.by(() => {
    if (!source) return null;
    const parts = [];
    if (source.dropped_media_count > 0) {
      parts.push(`${source.dropped_media_count} expired ${source.dropped_media_count === 1 ? 'image' : 'images'}`);
    }
    if (source.dropped_account_count > 0) {
      parts.push(`${source.dropped_account_count} disconnected ${source.dropped_account_count === 1 ? 'account' : 'accounts'}`);
    }
    return parts.length > 0 ? `${parts.join(' and ')} could not be carried over.` : null;
  });

  let remaining = $derived(5000 - form.body.length);
  let counterClass = $derived(remaining <= 0 ? 'text-red-600 font-medium' : remaining <= 100 ? 'text-amber-600 font-medium' : 'text-secondary-400');
</script>

<AppLayout title={source ? 'Reuse post' : 'Compose post'}>
  {#if calendars.length === 0}
    <Card>
      <div class="text-center py-6">
        <p class="text-secondary-500 mb-4">You need to connect an account before posting.</p>
        <Button href="/social_accounts/new">Connect an account</Button>
      </div>
    </Card>
  {:else}
    <div class="max-w-2xl flex flex-col gap-4">
      {#if source}
        <div class="rounded-xl border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-600 flex items-start gap-2">
          <Icon icon="mdi:content-copy" class="mt-0.5 shrink-0" />
          <div>
            Starting from <a use:inertia href={`/posts/${source.id}`} class="text-primary-600 hover:underline">an earlier post</a>. Nothing is saved until you publish or schedule it.
            {#if droppedNotice}
              <span class="block text-amber-700 mt-1">{droppedNotice}</span>
            {/if}
          </div>
        </div>
      {/if}

      <Card>
        <form onsubmit={(e) => { e.preventDefault(); submit(); }} class="flex flex-col gap-5">
          <Field label="Calendar" id="calendar" error={form.errors.calendar_id}>
            {#snippet children(describedBy)}
              <select id="calendar" class="input" value={form.calendar_id} onchange={(e) => selectCalendar(Number(e.target.value))} aria-describedby={describedBy}>
                {#each calendars as cal}
                  <option value={cal.id}>{cal.name}</option>
                {/each}
              </select>
            {/snippet}
          </Field>

          <Field label="What do you want to say?" id="body" error={form.errors.body}>
            {#snippet children(describedBy)}
              <textarea
                id="body"
                rows="5"
                class="input resize-y"
                bind:value={form.body}
                maxlength="5000"
                placeholder="Write your post…"
                required
                aria-describedby={describedBy}
              ></textarea>
              <span class="text-xs self-end {counterClass}">{remaining} characters left</span>
            {/snippet}
          </Field>

          <Field label="Images" id="images" error={clientErrors.media || form.errors.media}>
            {#snippet children(describedBy)}
              <div aria-describedby={describedBy}>
                <ImageUploader bind:media={form.media} bind:uploading={uploadingCount} />
              </div>
            {/snippet}
          </Field>

          <Field label="Post to" id="accounts" error={form.errors.social_account_ids}>
            {#if social_accounts.length === 0}
              <p class="text-sm text-secondary-500">This calendar has no connected accounts yet.</p>
            {:else}
              <div class="flex flex-col gap-2">
                {#if social_accounts.length > 1}
                  <label class="flex items-center gap-3 px-3 py-2 text-sm text-secondary-600 cursor-pointer">
                    <input type="checkbox" checked={allSelected} onchange={toggleAll} />
                    Select all
                  </label>
                {/if}
                {#each social_accounts as acc}
                  <label class="flex items-center gap-3 px-3 py-2 rounded-xl border border-secondary-200 cursor-pointer hover:bg-secondary-50 text-sm">
                    <input
                      type="checkbox"
                      checked={form.social_account_ids.includes(acc.id)}
                      onchange={() => toggleAccount(acc.id)}
                    />
                    <PlatformBadge platform={acc.platform} handle={acc.handle} />
                  </label>
                {/each}
              </div>
            {/if}
            {#snippet hint()}
              {#if form.social_account_ids.length === 0}
                <span class="text-xs text-secondary-400">Pick at least one account to post to.</span>
              {/if}
            {/snippet}
          </Field>

          <div class="flex flex-col gap-3">
            <label class="flex items-center gap-2 text-sm text-secondary-700">
              <input type="checkbox" bind:checked={scheduleLater} />
              Schedule for later
            </label>
            {#if scheduleLater}
              <Field label="Publish at" id="scheduled_at" error={clientErrors.scheduled_at || form.errors.scheduled_at}>
                {#snippet children(describedBy)}
                  <input
                    id="scheduled_at"
                    type="datetime-local"
                    class="input"
                    bind:value={form.scheduled_at}
                    min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                    aria-describedby={describedBy}
                  />
                {/snippet}
              </Field>
            {/if}
          </div>

          <div class="flex items-center gap-3">
            <Button type="submit" disabled={form.processing || uploadingCount > 0 || form.social_account_ids.length === 0}>
              <Icon icon={scheduleLater ? 'mdi:clock-outline' : 'mdi:send'} class="mr-1" />
              {form.processing ? 'Saving…' : uploadingCount > 0 ? 'Uploading…' : scheduleLater ? 'Schedule' : 'Publish now'}
            </Button>
            <Button href="/posts" variant="ghost">Cancel</Button>
          </div>
        </form>
      </Card>
    </div>
  {/if}
</AppLayout>
